import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BRAND } from "@/config/brand";
import { useSession } from "./SessionProvider";
import { fullName, STATUS_LABELS, type AccountStatus, type SessionUser } from "./types";
import { finaliserInscriptionSiBesoin, type FinalisationResultat } from "@/store/platform/inscriptionPaiement";

type FinalisationEtat = "idle" | "encours" | Exclude<FinalisationResultat, "rien">;

const STATUS_MESSAGES: Record<
  Exclude<AccountStatus, "actif">,
  { icon: string; title: string; text: string }
> = {
  en_attente: {
    icon: "ri-time-line",
    title: "Activation en cours",
    text: "Votre compte n'est pas encore actif. Après confirmation de votre e-mail, reconnectez-vous : votre accès s'active automatiquement, sans aucune validation manuelle.",
  },
  suspendu: {
    icon: "ri-pause-circle-line",
    title: "Compte suspendu",
    text: "L'accès à votre espace est temporairement suspendu. Contactez le support de la plateforme pour réactiver votre compte.",
  },
  desactive: {
    icon: "ri-forbid-2-line",
    title: "Compte désactivé",
    text: "Ce compte a été désactivé et ne peut plus accéder à son espace. Contactez le support si vous pensez qu'il s'agit d'une erreur.",
  },
  refuse: {
    icon: "ri-close-circle-line",
    title: "Inscription refusée",
    text: "Votre demande d'inscription a été refusée par un administrateur. Contactez le support de la plateforme si vous pensez qu'il s'agit d'une erreur.",
  },
};

/** Titre affiché selon la raison serveur renvoyée par l'offre de lancement. */
const RAISON_TITRES: Record<string, string> = {
  essai_deja_consomme: "Essai gratuit déjà utilisé",
  paiement_en_attente: "Paiement en cours de validation",
  abonnement_payant_actif: "Abonnement payant actif",
  profil_metier_manquant: "Profil incomplet",
  offre_indisponible: "Offre temporairement indisponible",
  identite_indisponible: "Compte incomplet",
  non_eligible: "Compte non concerné",
  non_connecte: "Session expirée",
  profil_inexistant: "Profil introuvable",
  activation_incomplete: "Activation en cours",
  erreur_activation: "Activation impossible",
  erreur_interne: "Activation impossible",
  activation_indisponible: "Activation à vérifier",
};

/**
 * Échecs d'ACTIVATION de l'offre de lancement (aucun paiement en jeu) : on
 * propose une relance de l'octroi automatique plutôt qu'une action de paiement.
 */
const CODES_ACTIVATION_TECHNIQUE = [
  "activation_incomplete",
  "erreur_activation",
  "erreur_interne",
  "activation_indisponible",
];

/** Écran affiché lorsqu'un compte bloqué tente d'accéder à un espace protégé. */
export default function AccessBlocked({ session }: { session: SessionUser }) {
  const { logout, refresh } = useSession();
  const navigate = useNavigate();
  const status = session.statut === "actif" ? "suspendu" : session.statut;
  const message = STATUS_MESSAGES[status];
  const [finalisation, setFinalisation] = useState<FinalisationEtat>("idle");

  // Raison réelle du blocage fournie par le serveur (offre de lancement non
  // attribuée, demande de paiement en attente…). Affichée à la place du message
  // générique pour ne jamais laisser un compte bloqué sans explication.
  const raisonSpecifique = status === "en_attente" ? session.raisonBlocage : undefined;
  const titre = (session.raisonCode && RAISON_TITRES[session.raisonCode]) || message.title;
  const texte = raisonSpecifique || message.text;
  const essaiDejaConsomme = status === "en_attente" && session.raisonCode === "essai_deja_consomme";
  const activationTechnique =
    status === "en_attente" &&
    !!session.raisonCode &&
    CODES_ACTIVATION_TECHNIQUE.includes(session.raisonCode);
  // À la première connexion d'un compte en attente : on transmet AUTOMATIQUEMENT
  // la demande de paiement choisie à l'inscription (preuve conservée côté
  // navigateur). Le client ne re-fournit jamais sa preuve. Idempotent côté serveur.
  useEffect(() => {
    if (status !== "en_attente" || essaiDejaConsomme || activationTechnique) return;
    let actif = true;
    setFinalisation("encours");
    void finaliserInscriptionSiBesoin().then((resultat) => {
      if (!actif) return;
      setFinalisation(resultat === "rien" ? "idle" : resultat);
    });
    return () => {
      actif = false;
    };
  }, [status, essaiDejaConsomme, activationTechnique]);

  const backToLogin = () => {
    logout();
    navigate("/connexion", { replace: true });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background-50 px-4 py-16">
      <div className="w-full max-w-md rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-700">
          <i className={`${message.icon} text-2xl`} aria-hidden="true" />
        </span>

        <h1 className="mt-5 font-heading text-xl font-semibold text-foreground-950">{titre}</h1>
        <p className="mt-2 text-sm text-foreground-600">{texte}</p>

        <dl className="mt-5 flex flex-col gap-2 rounded-md border border-background-200 bg-background-100 p-4 text-sm">
          <div className="flex items-center justify-between gap-3">
            <dt className="text-foreground-500">Compte</dt>
            <dd className="font-label font-medium text-foreground-900">{fullName(session)}</dd>
          </div>
          <div className="flex items-center justify-between gap-3">
            <dt className="text-foreground-500">E-mail</dt>
            <dd className="truncate font-label font-medium text-foreground-900">{session.email}</dd>
          </div>
          <div className="flex items-center justify-between gap-3">
            <dt className="text-foreground-500">Statut</dt>
            <dd className="font-label font-medium text-primary-700">{STATUS_LABELS[session.statut]}</dd>
          </div>
        </dl>

        {status === "en_attente" ? (
          <div className="mt-6 flex flex-col gap-2">
            {essaiDejaConsomme && (
              <p className="flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-3.5 py-2.5 text-xs leading-relaxed text-secondary-900">
                <i className="ri-information-line mt-0.5 text-sm" aria-hidden="true" />
                Cette offre de lancement est réservée à un premier essai par adresse e-mail. Contactez le support si vous
                pensez qu'il s'agit d'une erreur.
              </p>
            )}
            {activationTechnique && (
              <p className="flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-3.5 py-2.5 text-xs leading-relaxed text-accent-900">
                <i className="ri-refresh-line mt-0.5 text-sm" aria-hidden="true" />
                Aucun paiement n'est demandé ici : il s'agit de l'activation automatique de votre offre de lancement.
                Relancez l'opération ci-dessous.
              </p>
            )}
            {!essaiDejaConsomme && !activationTechnique && finalisation === "encours" && (
              <p className="flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3.5 py-2.5 text-xs text-foreground-700">
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-background-300 border-t-primary-500" />
                Transmission de votre demande de paiement…
              </p>
            )}
            {!essaiDejaConsomme && !activationTechnique && (finalisation === "envoye" || finalisation === "deja") && (
              <p className="flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-3.5 py-2.5 text-xs leading-relaxed text-secondary-900">
                <i className="ri-checkbox-circle-line mt-0.5 text-sm" aria-hidden="true" />
                Votre demande de paiement a bien été enregistrée. Elle est en attente de validation par notre équipe :
                aucune nouvelle saisie n'est nécessaire.
              </p>
            )}
            {!essaiDejaConsomme && !activationTechnique && finalisation === "preuve-manquante" && (
              <p className="flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-3.5 py-2.5 text-xs leading-relaxed text-accent-900">
                <i className="ri-attachment-2 mt-0.5 text-sm" aria-hidden="true" />
                Nous n'avons pas retrouvé votre preuve de paiement sur cet appareil. Joignez-la une seule fois pour
                finaliser votre demande.
              </p>
            )}
            {!essaiDejaConsomme && !activationTechnique && finalisation === "echec" && (
              <p className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50/60 px-3.5 py-2.5 text-xs leading-relaxed text-primary-800">
                <i className="ri-error-warning-line mt-0.5 text-sm" aria-hidden="true" />
                La transmission automatique a échoué. Vous pouvez rejoindre votre paiement en un clic.
              </p>
            )}
            {activationTechnique ? (
              <button
                type="button"
                onClick={() => void refresh()}
                className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-refresh-line text-base" aria-hidden="true" />
                Relancer l'activation
              </button>
            ) : (
              !essaiDejaConsomme && (
                <button
                  type="button"
                  onClick={() => navigate("/finaliser-paiement")}
                className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                  <i className="ri-secure-payment-line text-base" aria-hidden="true" />
                  {finalisation === "preuve-manquante" ? "Joindre ma preuve et finaliser" : "Finaliser mon paiement"}
                </button>
              )
            )}
            <button
              type="button"
              onClick={backToLogin}
              className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-logout-box-r-line text-base" aria-hidden="true" />
              Retour à la connexion
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={backToLogin}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-logout-box-r-line text-base" aria-hidden="true" />
            Retour à la connexion
          </button>
        )}

        <p className="mt-4 text-center text-xs text-foreground-500">
          {BRAND.name} — accès réservé aux comptes actifs.
        </p>
      </div>
    </div>
  );
}